namespace $ {

	export type $shm_dashboard_telemetry_sentry_context = {
		version: string
		user_id: string
		section: string
		dashboard_id: string
	}

	type $shm_dashboard_telemetry_sentry_pending = {
		node: Element
		started: number
		reports: number
		next_report: number
	}

	type $shm_dashboard_telemetry_sentry_fiber = {
		id: string
		task: string
		host: string
		args: readonly unknown[]
		stack: string
		children: $shm_dashboard_telemetry_sentry_fiber[]
	}

	/** Sentry delivery and long-running $mol suspension diagnostics for Monitoring+. */
	export class $shm_dashboard_telemetry_sentry extends $mol_object {

		private static views = new WeakMap< Element, $mol_view >()
		private static started = false
		private static observer: MutationObserver | null = null
		private static timer: ReturnType< typeof setInterval > | null = null
		private static pending = new Map< Element, $shm_dashboard_telemetry_sentry_pending >()
		private static recent = new Map< string, number >()
		private static sent_times: number[] = []
		private static suppressed = new Map< string, number >()
		private static context: $shm_dashboard_telemetry_sentry_context = {
			version: '-',
			user_id: '',
			section: '',
			dashboard_id: '',
		}

		static sdk() {
			return ( this.$.$mol_dom_context as any ).Sentry as any
		}

		static integrations_safe( defaults: readonly any[] ) {
			return defaults.filter( integration => integration?.name !== 'BrowserApiErrors' && integration?.name !== 'Breadcrumbs' )
		}

		static view_probe_install() {
			const proto = this.$.$mol_view.prototype as any
			const marker = '$' + 'shm_dashboard_telemetry_sentry_dom_node_external'
			if( proto[ marker ] ) return
			const dom_node_external = proto.dom_node_external
			proto.dom_node_external = function( this: $mol_view, next?: Element ) {
				const node = dom_node_external.call( this, next ) as Element
				$shm_dashboard_telemetry_sentry.views.set( node, this )
				return node
			}
			proto[ marker ] = true
		}

		static dsn() {
			const document = this.$.$mol_dom_context.document
			return document?.querySelector< HTMLMetaElement >( 'meta[name="dashboard-sentry-dsn"]' )?.content.trim() ?? ''
		}

		static start() {
			if( this.started ) return
			this.started = true
			this.view_probe_install()
			this.sdk_start()
			if( !this.sdk() ) this.$.$mol_dom_context.addEventListener?.(
				'dashboard-sentry-ready',
				()=> this.sdk_start(),
				{ once: true },
			)
			this.monitor_start()
		}

		static sdk_start() {
			const sdk = this.sdk()
			const dsn = this.dsn()
			if( sdk?.init && dsn && !sdk.isInitialized?.() ) {
				sdk.init({
					dsn,
					environment: this.environment(),
					sendDefaultPii: false,
					integrations: ( defaults: readonly any[] )=> this.integrations_safe( defaults ),
					tracesSampleRate: 0,
					replaysSessionSampleRate: 0,
					replaysOnErrorSampleRate: 0,
					maxBreadcrumbs: 100,
					normalizeDepth: 6,
					beforeSend: ( event: any )=> this.event_enrich( event ),
				})
			}
			sdk?.addEventProcessor?.( ( event: any )=> this.event_enrich( event ) )
			this.context_apply( sdk )
		}

		static environment() {
			const host = this.$.$mol_dom_context.location?.hostname ?? ''
			if( host === 'localhost' || host === '127.0.0.1' ) return 'development'
			if( host.includes( '158-160-195-195' ) ) return 'prerelease'
			return 'production'
		}

		static context_update( next: $shm_dashboard_telemetry_sentry_context ) {
			this.context = { ... next }
			this.context_apply()
		}

		static context_apply( sdk = this.sdk() ) {
			if( !sdk ) return
			const next = this.context
			sdk.setUser?.( next.user_id ? { id: next.user_id } : null )
			sdk.setTags?.({
				app_version: next.version,
				section: next.section,
				dashboard_id: next.dashboard_id,
			})
			sdk.setContext?.( 'monitoring_plus', {
				... next,
				url: this.$.$mol_dom_context.location?.href ?? '',
			})
		}

		static event_enrich( event: any ) {
			const context = this.context
			if( context.version && context.version !== '-' ) event.release = `monitoring-plus@${ context.version }`
			event.tags = {
				... event.tags,
				app_version: context.version,
				section: context.section,
				dashboard_id: context.dashboard_id,
			}
			return event
		}

		static monitor_start() {
			const document = this.$.$mol_dom_context.document
			if( !document?.documentElement || typeof MutationObserver === 'undefined' ) return
			this.scan( document.documentElement )
			this.observer = new MutationObserver( mutations => {
				for( const mutation of mutations ) {
					if( mutation.type === 'attributes' ) this.track( mutation.target as Element )
				}
			} )
			this.observer.observe( document.documentElement, {
				attributes: true,
				attributeFilter: [ 'mol_view_error' ],
				subtree: true,
			} )
			this.timer = setInterval( ()=> {
				this.scan( document.documentElement )
				this.check()
			}, 1000 )
		}

		static scan( root: Element ) {
			this.track( root )
			for( const node of root.querySelectorAll( '[mol_view_error="Promise"], [mol_view_error="$mol_promise_blocker"]' ) ) {
				this.track( node )
			}
		}

		static track( node: Element ) {
			const error = node.getAttribute( 'mol_view_error' )
			const waiting = error === 'Promise' || error === '$mol_promise_blocker'
			const current = this.pending.get( node )
			if( waiting ) {
				if( !current ) {
					const now = Date.now()
					this.pending.set( node, { node, started: now, reports: 0, next_report: now + 10_000 } )
				}
				return
			}
			if( !current ) return
			this.pending.delete( node )
			if( current.reports ) this.sdk()?.addBreadcrumb?.({
				category: 'mol.promise',
				level: 'info',
				message: 'Suspension resolved',
				data: { duration_ms: Date.now() - current.started, view: this.node_name( node ) },
			})
		}

		static check( now = Date.now() ) {
			for( const [ node, wait ] of this.pending ) {
				if( !node.isConnected ) {
					this.pending.delete( node )
					continue
				}
				if( now < wait.next_report ) continue
				if( this.$.$mol_dom_context.document?.visibilityState === 'hidden' ) continue
				this.promise_report( wait, now )
				wait.reports++
				const duration = now - wait.started
				wait.next_report = wait.started + ( duration < 30_000 ? 30_000 : duration < 120_000 ? 120_000 : duration + 120_000 )
			}
		}

		static promise_report( wait: $shm_dashboard_telemetry_sentry_pending, now = Date.now() ) {
			const snapshot = this.pending_snapshot( wait.node )
			const leaf = this.fiber_leaf( snapshot.fiber )
			const signature = leaf?.id || snapshot.view || wait.node.id || 'unknown'
			if( !this.send_allowed( signature, now ) ) {
				this.suppressed.set( signature, ( this.suppressed.get( signature ) ?? 0 ) + 1 )
				return
			}

			const duration = now - wait.started
			const error = new Error( `Long $mol Promise in ${ signature } (${ Math.round( duration / 1000 ) }s)` )
			error.name = 'MolPromiseStalledError'
			if( leaf?.stack ) error.stack = `${ error.name }: ${ error.message }\n${ leaf.stack }`

			const sdk = this.sdk()
			if( !sdk?.captureException ) return
			const send = ( scope: any )=> {
				scope?.setLevel?.( duration >= 30_000 ? 'error' : 'warning' )
				scope?.setFingerprint?.([ 'mol-promise-stalled', signature ] )
				scope?.setContext?.( 'mol_promise', {
					duration_ms: duration,
					report_number: wait.reports + 1,
					suppressed_repeats: this.suppressed.get( signature ) ?? 0,
					view: snapshot.view,
					node_id: wait.node.id,
					pending_views: this.pending.size,
					fiber_chain: this.fiber_lines( snapshot.fiber ),
					fiber: snapshot.fiber,
					online: this.$.$mol_dom_context.navigator?.onLine ?? true,
					visibility: this.$.$mol_dom_context.document?.visibilityState ?? '',
					url: this.$.$mol_dom_context.location?.href ?? '',
				} )
				this.suppressed.delete( signature )
				sdk.captureException( error )
			}
			if( sdk.withScope ) sdk.withScope( send )
			else send( null )
		}

		static send_allowed( signature: string, now = Date.now() ) {
			if( now - ( this.recent.get( signature ) ?? 0 ) < 2_000 ) return false
			this.sent_times = this.sent_times.filter( time => now - time < 60_000 )
			if( this.sent_times.length >= 100 ) return false
			this.recent.set( signature, now )
			this.sent_times.push( now )
			return true
		}

		static pending_snapshot( node: Element ) {
			const view = this.views.get( node ) ?? null
			const fiber = view ? Object.getOwnPropertyDescriptor( view, 'dom_tree()' )?.value : null
			return {
				view: view ? String( view ) : this.node_name( node ),
				fiber: this.fiber_snapshot( fiber, new Set, 0 ),
			}
		}

		static fiber_snapshot( fiber: any, seen: Set< any >, depth: number ): $shm_dashboard_telemetry_sentry_fiber | null {
			if( !fiber || seen.has( fiber ) || depth >= 12 ) return null
			seen.add( fiber )
			const children = [] as $shm_dashboard_telemetry_sentry_fiber[]
			const data = Array.isArray( fiber.data ) ? fiber.data : []
			for( let index = Number( fiber.pub_from ) || 0; index < ( Number( fiber.sub_from ) || 0 ); index += 2 ) {
				const child = data[ index ]
				if( !child || !this.$.$mol_promise_like( child.cache ) ) continue
				const snapshot = this.fiber_snapshot( child, seen, depth + 1 )
				if( snapshot ) children.push( snapshot )
			}
			return {
				id: this.safe_text( fiber ),
				task: String( fiber.task?.name ?? '' ),
				host: String( fiber.host?.constructor?.name ?? '' ),
				args: this.args_snapshot( fiber.args ),
				stack: fiber.cache?.stack ? String( fiber.cache.stack ) : '',
				children,
			}
		}

		static fiber_leaf( fiber: $shm_dashboard_telemetry_sentry_fiber | null ): $shm_dashboard_telemetry_sentry_fiber | null {
			if( !fiber ) return null
			return fiber.children.length ? this.fiber_leaf( fiber.children[0] ) : fiber
		}

		static fiber_lines( fiber: $shm_dashboard_telemetry_sentry_fiber | null, prefix = '' ): string[] {
			if( !fiber ) return []
			const lines = [ `${ prefix }${ fiber.id }` ]
			for( const child of fiber.children ) lines.push( ... this.fiber_lines( child, `${ prefix }  ` ) )
			return lines
		}

		static args_snapshot( args: unknown ) {
			if( !Array.isArray( args ) ) return []
			return args.slice( 0, 8 ).map( arg => {
				if( arg === null || arg === undefined ) return arg
				if( typeof arg === 'string' ) return arg.slice( 0, 200 )
				if( typeof arg === 'number' || typeof arg === 'boolean' ) return arg
				return `<${ ( arg as any )?.constructor?.name ?? typeof arg }>`
			} )
		}

		static safe_text( value: unknown ) {
			try {
				return String( value ).slice( 0, 500 )
			} catch {
				return '<unknown fiber>'
			}
		}

		static node_name( node: Element ) {
			const names = node.getAttributeNames().filter( name => name.startsWith( 'shm_' ) || name.startsWith( 'mol_' ) )
			return names[0] || node.tagName.toLowerCase()
		}

		static capture_test( message = 'Monitoring Plus Sentry test' ) {
			const error = new Error( message )
			error.name = 'MonitoringPlusSentryTestError'
			return this.sdk()?.captureException?.( error ) ?? null
		}

	}

	$shm_dashboard_telemetry_sentry.view_probe_install()

}
